import { useNavigate } from "@tanstack/react-router";
import { ShoppingBag, Star, ThumbsDown, ThumbsUp } from "lucide-react";
import { FavoriteButton } from "./FavoriteButton";

interface VendorData {
    id: number;
    name: string;
    isApproved: boolean;
    website: string;
    category?: string | null;
    rating?: number | null;
    commentCount: number;
    likesVotesCount: number;
    unlikesVotesCount: number;
}

export const VendorCard = ({ vendor, target }: { vendor: VendorData; target?: string }) => {
    const navigate = useNavigate();
    const vendorUrl = `/vendors/${vendor.id}`;

    const handleClick = () => {
        if (target === "_blank") {
            window.open(vendorUrl, "_blank", "noopener,noreferrer");
        } else {
            navigate({ to: "/vendors/$id", params: { id: String(vendor.id) } });
        }
    };

    return (
        <div
            role="link"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick();
                }
            }}
            className="cursor-pointer rounded-2xl border border-yellow-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
        >
            <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <ShoppingBag className="h-4 w-4 text-yellow-600" />
                    <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-medium text-yellow-700">
                        Vendor
                    </span>
                </div>
                <FavoriteButton target={{ type: "vendor", id: vendor.id }} size={16} />
            </div>
            <h3 className="text-base font-semibold text-gray-900">{vendor.name}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                {vendor.category && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {vendor.category}
                    </span>
                )}
                <a
                    href={vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {vendor.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                </a>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
                {vendor.rating != null && vendor.rating > 0 && (
                    <span className="flex items-center gap-1">
                        <Star className="h-3 w-3 text-yellow-500" fill="currentColor" />
                        {vendor.rating.toFixed(1)}
                    </span>
                )}
                <span className="flex items-center gap-1">
                    <ThumbsUp className="h-3 w-3" />
                    {vendor.likesVotesCount}
                </span>
                <span className="flex items-center gap-1">
                    <ThumbsDown className="h-3 w-3" />
                    {vendor.unlikesVotesCount}
                </span>
                <span>{vendor.commentCount} comments</span>
            </div>
        </div>
    );
};
